// VNEC v9 — pages/approvals.js
// 2-level approval with four-eyes enforced at DB (via approve_record RPC)
import { supabase, getCurrentUser, submitForApproval, rejectRecord, approveRecord } from '../lib/supabase.js';
import { formatVND } from '../lib/forms.js';

const TABLES = {
  hopdong: { table: 'hop_dong',        label: 'Hợp đồng' },
  plhd:    { table: 'plhd',            label: 'Phụ lục'   },
  donhang: { table: 'don_hang',        label: 'Đơn hàng'  },
  chiphi:  { table: 'chi_phi',         label: 'Chi phí'   },
  pyc:     { table: 'pyctt',           label: 'Phiếu YCTT' }
};
const ST_LABELS = {
  draft: 'Nháp',
  pending_l1: 'Chờ duyệt L1',
  pending_l2: 'Chờ duyệt L2',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy'
};
const ST_CLASS  = {
  draft: 'draft',
  pending_l1: 'pending',
  pending_l2: 'l1',
  approved: 'approved',
  rejected: 'rejected',
  cancelled: 'rejected'
};

let _state = { currentTab: 'hopdong', user: null, data: { hopdong:[], plhd:[], donhang:[], chiphi:[], pyc:[] }, selected: new Set() };

export function renderApprovals() {
  window.__pageInit = initApprovals;
  return `
    <div class="appr-wrap">
      <div class="page-hd">
        <h2>Phê duyệt 2 cấp</h2>
        <button class="btn btn-ghost btn-sm" id="appr-refresh">⟳ Refresh</button>
      </div>
      <div class="summary-grid" id="appr-summary"></div>
      <div class="tabs" id="appr-tabs">
        <button class="tab active" data-tab="hopdong">Hợp đồng <span class="tab-cnt" id="cnt-hopdong">0</span></button>
        <button class="tab" data-tab="plhd">Phụ lục <span class="tab-cnt" id="cnt-plhd">0</span></button>
        <button class="tab" data-tab="donhang">Đơn hàng <span class="tab-cnt" id="cnt-donhang">0</span></button>
        <button class="tab" data-tab="chiphi">Chi phí <span class="tab-cnt" id="cnt-chiphi">0</span></button>
        <button class="tab" data-tab="pyc">Phiếu YCTT <span class="tab-cnt" id="cnt-pyc">0</span></button>
      </div>
      <div class="st-filter" id="st-filter">
        <label><input type="checkbox" value="pending_l1" checked> Chờ duyệt</label>
        <label><input type="checkbox" value="pending_l2" checked> Duyệt L1</label>
        <label><input type="checkbox" value="draft"> Nháp</label>
        <label><input type="checkbox" value="rejected"> Từ chối</label>
        <label><input type="checkbox" value="approved"> Đã duyệt</label>
      </div>
      <div id="bulk-bar" style="display:none;background:#FFF8E1;border:1.5px solid #FFCC00;border-radius:4px;padding:8px 12px;margin-bottom:8px;display:none;align-items:center;gap:10px">
        <span style="font-weight:700;color:#000066">Đã chọn <span id="bulk-count">0</span> bản ghi:</span>
        <button class="btn btn-sm" id="bulk-l1" style="background:#0F6E56;color:#fff">✓ Duyệt L1 hàng loạt</button>
        <button class="btn btn-sm" id="bulk-l2" style="background:#185FA5;color:#fff">✓ Duyệt L2 hàng loạt</button>
        <button class="btn btn-sm" id="bulk-reject" style="background:#A32D2D;color:#fff">✕ Từ chối hàng loạt</button>
        <button class="btn btn-ghost btn-sm" id="bulk-clear">Bỏ chọn tất cả</button>
        <span id="bulk-progress" class="muted" style="margin-left:auto"></span>
      </div>
      <div id="appr-table" class="appr-table-wrap"></div>
      <div id="appr-modal" class="modal-overlay" style="display:none"></div>
    </div>`;
}

async function initApprovals() {
  _state.user = await getCurrentUser();
  document.getElementById('appr-refresh').addEventListener('click', loadAll);
  document.querySelectorAll('#appr-tabs .tab').forEach(btn => {
    btn.addEventListener('click', () => switchTab(btn.dataset.tab));
  });
  document.querySelectorAll('#st-filter input').forEach(cb => {
    cb.addEventListener('change', renderTable);
  });
  document.getElementById('bulk-l1').addEventListener('click', () => bulkApprove(1));
  document.getElementById('bulk-l2').addEventListener('click', () => bulkApprove(2));
  document.getElementById('bulk-reject').addEventListener('click', bulkReject);
  document.getElementById('bulk-clear').addEventListener('click', () => { _state.selected.clear(); renderTable(); });
  await loadAll();
}

async function loadAll() {
  const tbl = document.getElementById('appr-table');
  tbl.innerHTML = '<div class="loading">Đang tải…</div>';
  try {
    const [hd, pl, dh, cp, pyc] = await Promise.all([
      loadGrouped('hop_dong', ['ma_hd']),
      loadGrouped('plhd', ['ma_hd']),
      loadGrouped('don_hang', ['ma_hd']),
      loadGrouped('chi_phi', ['ma_hd','dot']),
      loadGrouped('pyctt', ['so_pyctt'])
    ]);
    _state.data.hopdong = hd.filter(g => g.ma_hd && !g.ma_hd.includes('/PL-') && !g.ma_hd.includes('/DH-'));
    _state.data.plhd    = pl;
    _state.data.donhang = dh;
    _state.data.chiphi  = cp;
    _state.data.pyc     = pyc;
    renderSummary();
    renderTable();
  } catch (e) {
    tbl.innerHTML = `<div class="error-box">Lỗi: ${e.message}</div>`;
  }
}

async function loadGrouped(table, groupCols) {
  let selectCols = `id,${groupCols.join(',')},trang_thai,created_by,created_at,approved_l1_by,approved_l1_at,approved_l2_by,approved_l2_at,rejection_count,reject_reason`;
  if (table === 'hop_dong') selectCols += ',nha_thau,gia_tri';
  if (table === 'chi_phi')  selectCols += ',nha_thau,tong_tt,ten_cp';
  if (table === 'pyctt')    selectCols += ',ma_hd,ky_tt,noi_dung,chi_trong_ky,hinh_thuc_tt,loai_pyc,so_tien';
  if (table === 'plhd')     selectCols += ',so_plhd,ma_hd_goc,nha_thau,noi_dung,thanh_tien,gia_tri_thay_doi,loai_cp,ngay_ky';
  if (table === 'don_hang') selectCols += ',so_dh,ma_hd_khung,nha_thau,ngay_dh,tong_gia_tri';

  const { data, error } = await supabase
    .from(table).select(selectCols).is('deleted_at', null)
    .order('created_at', { ascending: false }).limit(5000);
  if (error) throw error;

  const map = new Map();
  (data || []).forEach(r => {
    const key = groupCols.map(c => r[c] ?? '').join('||');
    // Alias so_pyctt → ma_hd for display compat (pyctt group key is so_pyctt)
    if (table === 'pyctt' && !r.ma_hd && r.so_pyctt) r.ma_hd_display = r.so_pyctt;
    const ttVal = parseFloat(
      r.gia_tri ?? r.tong_tt ?? r.so_tien ?? r.chi_trong_ky
      ?? r.tong_gia_tri ?? r.thanh_tien ?? r.gia_tri_thay_doi ?? 0
    ) || 0;
    if (!map.has(key)) {
      const g = {
        _key: key, _ids: [r.id],
        trang_thai: r.trang_thai, created_by: r.created_by,
        approved_l1_by: r.approved_l1_by, approved_l2_by: r.approved_l2_by,
        approved_l1_at: r.approved_l1_at, approved_l2_at: r.approved_l2_at,
        created_at: r.created_at, so_dong: 1,
        tong_tt: ttVal,
        rejection_count: r.rejection_count || 0,
        reject_reason: r.reject_reason || ''
      };
      groupCols.forEach(c => g[c] = r[c]);
      if (table === 'hop_dong' || table === 'chi_phi') g.nha_thau = r.nha_thau;
      if (table === 'chi_phi') g.ten_cp = r.ten_cp;
      if (table === 'pyctt') {
        g.ma_hd = r.ma_hd || r.so_pyctt;
        g.so_phieu = r.so_pyctt;
        g.ky_tt = r.ky_tt; g.noi_dung = r.noi_dung; g.hinh_thuc_tt = r.hinh_thuc_tt; g.loai_pyc = r.loai_pyc;
      }
      if (table === 'plhd') {
        g.nha_thau = r.nha_thau;
        g.ma_hd_goc = r.ma_hd_goc;
        g.ngay_ky = r.ngay_ky;
      }
      if (table === 'don_hang') {
        g.nha_thau = r.nha_thau;
        g.ma_hd_khung = r.ma_hd_khung;
        g.ngay_dh = r.ngay_dh;
      }
      map.set(key, g);
    } else {
      const g = map.get(key);
      g._ids.push(r.id); g.so_dong++;
      g.tong_tt += ttVal;
    }
  });
  return Array.from(map.values());
}

function renderSummary() {
  const counts = { draft:0, pending_l1:0, pending_l2:0, approved:0, rejected:0 };
  ['hopdong','plhd','donhang','chiphi','pyc'].forEach(t => {
    _state.data[t].forEach(g => { counts[g.trang_thai] = (counts[g.trang_thai]||0) + 1; });
  });
  document.getElementById('appr-summary').innerHTML = `
    <div class="scard draft"><div class="sc-num">${counts.draft}</div><div class="sc-lbl">Nháp</div></div>
    <div class="scard pending"><div class="sc-num">${counts.pending_l1}</div><div class="sc-lbl">Chờ duyệt L1</div></div>
    <div class="scard l1"><div class="sc-num">${counts.pending_l2}</div><div class="sc-lbl">Chờ duyệt L2</div></div>
    <div class="scard approved"><div class="sc-num">${counts.approved}</div><div class="sc-lbl">Đã duyệt</div></div>
    <div class="scard rejected"><div class="sc-num">${counts.rejected}</div><div class="sc-lbl">Từ chối</div></div>`;
}

function getActiveStatuses() {
  return Array.from(document.querySelectorAll('#st-filter input:checked')).map(cb => cb.value);
}

function switchTab(tab) {
  _state.currentTab = tab;
  document.querySelectorAll('#appr-tabs .tab').forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
  renderTable();
}

function renderTable() {
  const filters = getActiveStatuses();
  ['hopdong','plhd','donhang','chiphi','pyc'].forEach(t => {
    document.getElementById('cnt-' + t).textContent = _state.data[t].filter(g => filters.includes(g.trang_thai)).length;
  });
  const rows = _state.data[_state.currentTab].filter(g => filters.includes(g.trang_thai));
  const wrap = document.getElementById('appr-table');
  if (!rows.length) { wrap.innerHTML = '<div class="empty">Không có bản ghi</div>'; updateBulkBar(); return; }

  const tab = _state.currentTab;
  let cols;
  if (tab === 'hopdong')       cols = [['Mã HĐ','ma_hd'],['Nhà Thầu','nha_thau'],['Dòng','so_dong','c'],['Tổng GT','tong_tt','r','money']];
  else if (tab === 'chiphi')   cols = [['Mã HĐ','ma_hd'],['Đợt','dot','c'],['Nhà Thầu','nha_thau'],['Tổng TT','tong_tt','r','money']];
  else if (tab === 'plhd')     cols = [['Mã PL','ma_hd'],['HĐ gốc','ma_hd_goc'],['Nhà Thầu','nha_thau'],['Dòng','so_dong','c'],['Tổng GT','tong_tt','r','money']];
  else if (tab === 'donhang')  cols = [['Mã ĐH','ma_hd'],['HĐ khung','ma_hd_khung'],['Nhà Thầu','nha_thau'],['Tổng GT','tong_tt','r','money']];
  else                          cols = [['Số Phiếu','so_phieu'],['Mã HĐ','ma_hd'],['Kỳ','ky_tt','c'],['Chi Kỳ','tong_tt','r','money']];

  let html = '<table class="appr-tbl"><thead><tr>';
  html += '<th class="c" style="width:30px"><input type="checkbox" id="bulk-select-all" title="Chọn tất cả dòng đang hiển thị"></th>';
  cols.forEach(c => html += `<th class="${c[2]||''}">${c[0]}</th>`);
  html += '<th class="c">Trạng thái</th><th class="c">Thao tác</th></tr></thead><tbody>';
  rows.forEach(g => {
    const isPending = g.trang_thai === 'pending_l1' || g.trang_thai === 'pending_l2';
    const checked = _state.selected.has(g._key) ? 'checked' : '';
    html += `<tr${_state.selected.has(g._key) ? ' style="background:#FFF8E1"' : ''}>`;
    html += `<td class="c">${isPending ? `<input type="checkbox" class="bulk-cb" data-key="${escH(g._key)}" ${checked}>` : ''}</td>`;
    cols.forEach(c => {
      const v = g[c[1]];
      const disp = c[3] === 'money' ? formatVND(v) : (v ?? '');
      html += `<td class="${c[2]||''}${c[3]==='money'?' money':''}">${escH(disp)}</td>`;
    });
    html += `<td class="c">${stBadge(g.trang_thai)}${rBadge(g.rejection_count, g.reject_reason)}</td>`;
    html += `<td class="c">${actionButtons(g)}</td></tr>`;
  });
  html += '</tbody></table>';
  wrap.innerHTML = html;

  // Action button handlers
  wrap.querySelectorAll('button[data-act]').forEach(btn => {
    btn.addEventListener('click', () => {
      const g = rows.find(r => r._key === btn.dataset.key);
      if (!g) return;
      const act = btn.dataset.act;
      if (act === 'review') showReviewModal(g);
      else if (act === 'submit') doSubmit(g);
      else if (act === 'l1') doApprove(g, 1);
      else if (act === 'l2') doApprove(g, 2);
      else if (act === 'reject') doReject(g);
      else if (act === 'reopen') doReopen(g);
    });
  });

  // Bulk checkbox handlers
  wrap.querySelectorAll('input.bulk-cb').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) _state.selected.add(cb.dataset.key);
      else _state.selected.delete(cb.dataset.key);
      renderTable();
    });
  });

  // Select all
  const selAll = document.getElementById('bulk-select-all');
  if (selAll) {
    selAll.addEventListener('change', () => {
      rows.filter(g => g.trang_thai === 'pending_l1' || g.trang_thai === 'pending_l2').forEach(g => {
        if (selAll.checked) _state.selected.add(g._key);
        else _state.selected.delete(g._key);
      });
      renderTable();
    });
  }

  updateBulkBar();
}

function updateBulkBar() {
  const bar = document.getElementById('bulk-bar');
  const cntEl = document.getElementById('bulk-count');
  if (!bar || !cntEl) return;
  const n = _state.selected.size;
  if (n === 0) {
    bar.style.display = 'none';
  } else {
    bar.style.display = 'flex';
    cntEl.textContent = n;
  }
}

async function bulkApprove(level) {
  const keys = [..._state.selected];
  if (!keys.length) return;
  const groups = _state.data[_state.currentTab].filter(g => keys.includes(g._key));
  // Filter only the right state for this level
  const targetState = level === 1 ? 'pending_l1' : 'pending_l2';
  const eligible = groups.filter(g => g.trang_thai === targetState);
  const skipped = groups.length - eligible.length;
  if (!eligible.length) {
    toast(`Không có bản ghi nào ở trạng thái ${targetState}`, 'err');
    return;
  }
  if (!confirm(`Duyệt L${level} cho ${eligible.length} bản ghi?${skipped > 0 ? ` (Bỏ qua ${skipped} bản ghi không đúng trạng thái)` : ''}`)) return;

  const def = TABLES[_state.currentTab];
  let success = 0, failed = 0, lastErr = '';
  const progEl = document.getElementById('bulk-progress');
  let done = 0;
  for (const g of eligible) {
    progEl.textContent = `Đang xử lý ${++done}/${eligible.length}...`;
    for (const id of g._ids) {
      try {
        await approveRecord(def.table, id, 'l' + level);
        success++;
      } catch (e) { failed++; lastErr = e.message; }
    }
  }
  progEl.textContent = '';
  if (success > 0) toast(`Đã duyệt L${level}: ${success} dòng${failed > 0 ? ` (${failed} lỗi: ${lastErr})` : ''}`, failed > 0 ? 'err' : 'ok');
  else toast(`Tất cả ${failed} dòng đều lỗi: ${lastErr}`, 'err');
  _state.selected.clear();
  loadAll();
}

async function bulkReject() {
  const keys = [..._state.selected];
  if (!keys.length) return;
  const groups = _state.data[_state.currentTab].filter(g => keys.includes(g._key));
  const eligible = groups.filter(g => g.trang_thai === 'pending_l1' || g.trang_thai === 'pending_l2');
  if (!eligible.length) { toast('Không có bản ghi pending để từ chối', 'err'); return; }
  const reason = await showRejectModal({ count: eligible.length, label: `Áp dụng cho ${eligible.length} bản ghi đã chọn` });
  if (!reason) return;

  const def = TABLES[_state.currentTab];
  let success = 0, failed = 0, lastErr = '';
  const progEl = document.getElementById('bulk-progress');
  let done = 0;
  for (const g of eligible) {
    progEl.textContent = `Đang xử lý ${++done}/${eligible.length}...`;
    for (const id of g._ids) {
      try {
        await rejectRecord(def.table, id, reason);
        success++;
      } catch (e) { failed++; lastErr = e.message; }
    }
  }
  progEl.textContent = '';
  if (success > 0) toast(`Đã từ chối: ${success} dòng${failed > 0 ? ` (${failed} lỗi: ${lastErr})` : ''}`, failed > 0 ? 'err' : 'ok');
  else toast(`Tất cả ${failed} dòng đều lỗi: ${lastErr}`, 'err');
  _state.selected.clear();
  loadAll();
}

function stBadge(st) { return `<span class="badge-st badge-${ST_CLASS[st]||'draft'}">${ST_LABELS[st]||st}</span>`; }
function rBadge(n, reason) {
  if (!n || n <= 0) return '';
  const title = reason ? `title="Lần từ chối gần nhất: ${escH(reason)}"` : '';
  return `<span class="rej-cnt" ${title}>×${n}</span>`;
}

// 2D: Reject reason template modal — returns Promise<string|null>
const REJECT_TEMPLATES = [
  'Sai mã loại CP / phân loại IFRS',
  'Thiếu chứng từ / hóa đơn / hợp đồng đính kèm',
  'Vượt ngân sách dự án — cần điều chỉnh',
  'Thông tin nhà thầu / NCC không khớp HĐ',
  'Sai số tiền / khối lượng / đơn giá',
  'Cần bổ sung diễn giải nội dung',
  'Chưa có phê duyệt / ý kiến cấp trên',
  'Trùng lặp với bản ghi đã có',
  'Sai kỳ thanh toán / sai dự án',
  'Khác (vui lòng ghi rõ bên dưới)'
];

function showRejectModal(opts) {
  const { count = 1, label = '' } = opts || {};
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:8px;width:520px;max-width:92vw;padding:18px 22px;box-shadow:0 8px 32px rgba(0,0,0,0.3)">
        <div style="font-size:16px;font-weight:700;color:#A32D2D;margin-bottom:4px">❌ Từ chối ${count > 1 ? count + ' bản ghi' : 'bản ghi'}</div>
        ${label ? `<div style="font-size:12px;color:#666;margin-bottom:12px">${label}</div>` : '<div style="margin-bottom:12px"></div>'}
        <label style="display:block;font-size:12px;font-weight:600;color:#1a1a2e;margin-bottom:4px">Mẫu lý do thông dụng</label>
        <select id="rj-tmpl" class="inp" style="width:100%;margin-bottom:10px">
          <option value="">— Chọn mẫu —</option>
          ${REJECT_TEMPLATES.map(t => `<option value="${t.replace(/"/g, '&quot;')}">${t}</option>`).join('')}
        </select>
        <label style="display:block;font-size:12px;font-weight:600;color:#1a1a2e;margin-bottom:4px">Lý do từ chối <span style="color:#A32D2D">*</span></label>
        <textarea id="rj-text" rows="4" class="inp" placeholder="Nhập lý do từ chối (tối thiểu 3 ký tự)" style="width:100%;font-family:inherit;resize:vertical"></textarea>
        <div style="font-size:11px;color:#888;margin-top:4px">Người tạo sẽ nhận thông báo với lý do này.</div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px">
          <button id="rj-cancel" class="btn btn-ghost">Hủy</button>
          <button id="rj-ok" class="btn" style="background:#A32D2D;color:#fff">Xác nhận từ chối</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const tmpl = overlay.querySelector('#rj-tmpl');
    const text = overlay.querySelector('#rj-text');
    tmpl.addEventListener('change', () => {
      if (tmpl.value) {
        text.value = text.value ? text.value + '\n' + tmpl.value : tmpl.value;
        tmpl.value = '';
        text.focus();
      }
    });
    overlay.querySelector('#rj-cancel').addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve(null);
    });
    overlay.querySelector('#rj-ok').addEventListener('click', () => {
      const val = text.value.trim();
      if (val.length < 3) {
        text.style.borderColor = '#A32D2D';
        text.focus();
        return;
      }
      document.body.removeChild(overlay);
      resolve(val);
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) { document.body.removeChild(overlay); resolve(null); }
    });
    setTimeout(() => text.focus(), 50);
  });
}

function actionButtons(g) {
  const key = g._key, st = g.trang_thai;
  const uId = _state.user?.id || '';
  const uEmail = (_state.user?.email || '').toLowerCase();
  const role = _state.user?.role || 'viewer';
  const isAdmin = role === 'admin';
  // V9: created_by / approved_*_by là UUID (ref user_profiles.id)
  const isCreator = (g.created_by || '') === uId;
  const isL1Same = (g.approved_l1_by || '') === uId;

  let h = `<button class="btn-mini view" data-act="review" data-key="${escH(key)}">Xem</button>`;
  if (st === 'draft' || st === 'rejected') h += `<button class="btn-mini submit" data-act="submit" data-key="${escH(key)}">Gửi duyệt</button>`;
  if (st === 'pending_l1') {
    const blocked = isCreator && !isAdmin;
    h += `<button class="btn-mini approve" data-act="l1" data-key="${escH(key)}"${blocked?' disabled title="Không thể tự duyệt"':''}>Duyệt L1</button>`;
    h += `<button class="btn-mini reject" data-act="reject" data-key="${escH(key)}"${blocked?' disabled':''}>Từ chối</button>`;
  }
  if (st === 'pending_l2') {
    const blocked = (isCreator || isL1Same) && !isAdmin;
    const canL2 = role !== 'approver_l1';
    const title = !canL2 ? 'Không có quyền L2' : isL1Same ? 'Four-eyes: bạn đã duyệt L1' : isCreator ? 'Không thể tự duyệt' : '';
    h += `<button class="btn-mini approve-l2" data-act="l2" data-key="${escH(key)}"${(blocked||!canL2)?` disabled title="${title}"`:''}>Duyệt L2</button>`;
    h += `<button class="btn-mini reject" data-act="reject" data-key="${escH(key)}"${blocked?' disabled':''}>Từ chối</button>`;
  }
  if (st === 'rejected') h += `<button class="btn-mini reopen" data-act="reopen" data-key="${escH(key)}">Mở lại</button>`;
  return h;
}

// ── ACTIONS ──
async function doSubmit(g) {
  if (!confirm(`Gửi duyệt ${rowLabel(g)} (${g.so_dong} dòng)?`)) return;
  const def = TABLES[_state.currentTab];
  let success = 0, failed = 0, lastErr = '';
  for (const id of g._ids) {
    try {
      await submitForApproval(def.table, id);
      success++;
    } catch (e) { failed++; lastErr = e.message; }
  }
  if (success > 0) toast(`Đã gửi duyệt: ${success}/${g._ids.length} dòng`, 'ok');
  if (failed > 0) toast(`${failed} dòng lỗi: ${lastErr}`, 'err');
  loadAll();
}

async function doApprove(g, level) {
  if (!confirm(`Duyệt L${level} ${rowLabel(g)}?`)) return;
  const def = TABLES[_state.currentTab];
  let success = 0, failed = 0, lastErr = '';
  for (const id of g._ids) {
    try {
      await approveRecord(def.table, id, 'l' + level);
      success++;
    } catch (e) { failed++; lastErr = e.message; }
  }
  if (success > 0) toast(`Đã duyệt L${level}: ${success}/${g._ids.length} dòng`, 'ok');
  if (failed > 0) toast(`${failed} dòng lỗi: ${lastErr}`, 'err');
  loadAll();
}

async function doReject(g) {
  const reason = await showRejectModal({ count: 1, label: rowLabel(g) });
  if (!reason) return;
  const def = TABLES[_state.currentTab];
  let success = 0, failed = 0, lastErr = '';
  for (const id of g._ids) {
    try {
      await rejectRecord(def.table, id, reason);
      success++;
    } catch (e) { failed++; lastErr = e.message; }
  }
  if (success > 0) toast(`Đã từ chối: ${success}/${g._ids.length} dòng`, 'ok');
  if (failed > 0) toast(`${failed} dòng lỗi: ${lastErr}`, 'err');
  loadAll();
}

async function doReopen(g) {
  if (!confirm(`Mở lại ${rowLabel(g)} về Nháp?`)) return;
  // Reopen từ rejected → draft: RLS update_own_draft policy cho phép
  try {
    const def = TABLES[_state.currentTab];
    const { error } = await supabase.from(def.table)
      .update({ trang_thai: 'draft' }).in('id', g._ids).eq('trang_thai', 'rejected');
    if (error) throw error;
    toast('Đã mở lại', 'ok');
    loadAll();
  } catch (e) { toast('Lỗi: ' + e.message, 'err'); }
}

function rowLabel(g) {
  if (_state.currentTab === 'hopdong') return g.ma_hd;
  if (_state.currentTab === 'chiphi') return `${g.ma_hd} — Đợt ${g.dot}`;
  if (_state.currentTab === 'plhd') return g.ma_hd;
  if (_state.currentTab === 'donhang') return g.ma_hd;
  return g.so_phieu;
}

// ── REVIEW MODAL ──
async function showReviewModal(g) {
  const modal = document.getElementById('appr-modal');
  modal.style.display = 'flex';
  modal.innerHTML = `<div class="modal-box"><div class="m-hd"><div class="m-title">${rowLabel(g)}</div><button class="m-close" id="mc">×</button></div><div class="m-body" id="mb">Đang tải…</div></div>`;
  document.getElementById('mc').addEventListener('click', () => modal.style.display = 'none');
  modal.addEventListener('click', e => { if (e.target === modal) modal.style.display = 'none'; });

  try {
    const def = TABLES[_state.currentTab];

    // 1. Resolve user names for created_by, approved_l1_by, approved_l2_by UUIDs
    const uuids = [g.created_by, g.approved_l1_by, g.approved_l2_by].filter(Boolean);
    const nameMap = {};
    if (uuids.length) {
      const { data: profs } = await supabase.from('user_profiles')
        .select('id, full_name, email').in('id', uuids);
      (profs || []).forEach(p => { nameMap[p.id] = p.full_name || p.email; });
    }
    const nameOf = (id) => id ? (nameMap[id] || id.slice(0, 8) + '…') : '—';

    // 2. Load items: for hopdong → hop_dong_items; for chiphi → chi_phi rows; for pyctt → header + linked chi_phi
    let items = [];
    if (_state.currentTab === 'hopdong') {
      // g._ids contains hop_dong.id UUIDs — fetch children from hop_dong_items
      const { data, error } = await supabase.from('hop_dong_items')
        .select('*').in('hop_dong_id', g._ids).order('sort_order');
      if (error) throw error;
      items = data || [];
    } else if (_state.currentTab === 'chiphi') {
      // Each chi_phi row IS an item
      const { data, error } = await supabase.from(def.table).select('*').in('id', g._ids);
      if (error) throw error;
      items = data || [];
    } else if (_state.currentTab === 'plhd') {
      // plhd is flat — each row IS an item (noi_dung, don_vi, so_luong, don_gia, thanh_tien, loai_cp)
      const { data, error } = await supabase.from('plhd').select('*').in('id', g._ids);
      if (error) throw error;
      items = data || [];
    } else if (_state.currentTab === 'donhang') {
      // don_hang has children in don_hang_items
      const { data, error } = await supabase.from('don_hang_items')
        .select('*').in('don_hang_id', g._ids).order('sort_order');
      if (error) throw error;
      items = data || [];
    } else if (_state.currentTab === 'pyc') {
      // PYC header + linked cost rows via ma_chi_tiet
      const { data: pycData, error: e1 } = await supabase.from('pyctt').select('*').in('id', g._ids);
      if (e1) throw e1;
      items = pycData || [];
    }

    document.getElementById('mb').innerHTML = `
      <div class="trail">${mkTrail(g, nameOf)}</div>
      <div class="dg">
        ${dc('Người tạo', `${nameOf(g.created_by)} • ${fmtDate(g.created_at)}`)}
        ${g.approved_l1_by ? dc('Duyệt L1', `${nameOf(g.approved_l1_by)} • ${fmtDate(g.approved_l1_at)}`) : ''}
        ${g.approved_l2_by ? dc('Duyệt L2', `${nameOf(g.approved_l2_by)} • ${fmtDate(g.approved_l2_at)}`) : ''}
        ${g.rejection_count > 0 ? dc('Từ chối', `Lần ${g.rejection_count} — ${g.reject_reason || ''}`, 'warn') : ''}
      </div>
      ${renderItemsTable(items)}`;
  } catch (e) {
    document.getElementById('mb').innerHTML = `<div class="error-box">Lỗi: ${e.message}</div>`;
  }
}

function renderItemsTable(items) {
  if (!items || !items.length) return '<p class="muted center">Không có chi tiết</p>';
  if (_state.currentTab === 'hopdong') {
    // items = hop_dong_items rows (noi_dung, don_vi, so_luong, don_gia, thanh_tien GENERATED, loai_cp)
    let h = '<table class="mt"><thead><tr><th>#</th><th>Nội dung</th><th>ĐVT</th><th class="r">SL</th><th class="r">Đơn giá</th><th class="r">Thành tiền</th><th>Loại CP</th></tr></thead><tbody>';
    let total = 0;
    items.forEach((it, i) => {
      const tt = Number(it.thanh_tien) || 0; total += tt;
      h += `<tr><td class="c">${i + 1}</td><td>${escH(it.noi_dung || '')}</td><td>${escH(it.don_vi || '')}</td><td class="r">${formatVND(it.so_luong || 0)}</td><td class="r">${formatVND(it.don_gia || 0)}</td><td class="r money">${formatVND(tt)}</td><td class="small">${escH(it.loai_cp || '')}</td></tr>`;
    });
    h += `<tr class="ttl"><td colspan="5" class="r">TỔNG</td><td class="r money">${formatVND(total)}</td><td></td></tr></tbody></table>`;
    return h;
  }
  if (_state.currentTab === 'chiphi') {
    // items = chi_phi rows (ten_cp, so_luong, don_gia, thanh_tien, tong_tt, loai_cp, phan_loai)
    let h = '<table class="mt"><thead><tr><th>#</th><th>Nội dung</th><th>ĐVT</th><th class="r">SL</th><th class="r">Đơn giá</th><th class="r">Thành tiền</th><th>Loại CP</th></tr></thead><tbody>';
    let total = 0;
    items.forEach((it, i) => {
      const tt = Number(it.tong_tt || it.thanh_tien) || 0; total += tt;
      h += `<tr><td class="c">${i + 1}</td><td>${escH(it.ten_cp || '')}</td><td>${escH(it.don_vi || '')}</td><td class="r">${formatVND(it.so_luong || 0)}</td><td class="r">${formatVND(it.don_gia || 0)}</td><td class="r money">${formatVND(tt)}</td><td class="small">${escH(it.loai_cp || '')}</td></tr>`;
    });
    h += `<tr class="ttl"><td colspan="5" class="r">TỔNG</td><td class="r money">${formatVND(total)}</td><td></td></tr></tbody></table>`;
    return h;
  }
  if (_state.currentTab === 'plhd') {
    // plhd flat rows: noi_dung, don_vi, so_luong, don_gia, thanh_tien, loai_cp
    let h = '<table class="mt"><thead><tr><th>#</th><th>Nội dung</th><th>ĐVT</th><th class="r">SL</th><th class="r">Đơn giá</th><th class="r">Thành tiền</th><th>Loại CP</th></tr></thead><tbody>';
    let total = 0;
    items.forEach((it, i) => {
      const tt = Number(it.thanh_tien || it.gia_tri_thay_doi) || 0; total += tt;
      h += `<tr><td class="c">${i + 1}</td><td>${escH(it.noi_dung || '')}</td><td>${escH(it.don_vi || '')}</td><td class="r">${formatVND(it.so_luong || 0)}</td><td class="r">${formatVND(it.don_gia || 0)}</td><td class="r money">${formatVND(tt)}</td><td class="small">${escH(it.loai_cp || '')}</td></tr>`;
    });
    h += `<tr class="ttl"><td colspan="5" class="r">TỔNG</td><td class="r money">${formatVND(total)}</td><td></td></tr></tbody></table>`;
    return h;
  }
  if (_state.currentTab === 'donhang') {
    // items = don_hang_items children
    let h = '<table class="mt"><thead><tr><th>#</th><th>Nội dung</th><th>ĐVT</th><th class="r">SL</th><th class="r">Đơn giá</th><th class="r">Thành tiền</th><th>Loại CP</th></tr></thead><tbody>';
    let total = 0;
    items.forEach((it, i) => {
      const tt = Number(it.thanh_tien) || 0; total += tt;
      h += `<tr><td class="c">${i + 1}</td><td>${escH(it.noi_dung || '')}</td><td>${escH(it.don_vi || '')}</td><td class="r">${formatVND(it.so_luong || 0)}</td><td class="r">${formatVND(it.don_gia || 0)}</td><td class="r money">${formatVND(tt)}</td><td class="small">${escH(it.loai_cp || '')}</td></tr>`;
    });
    h += `<tr class="ttl"><td colspan="5" class="r">TỔNG</td><td class="r money">${formatVND(total)}</td><td></td></tr></tbody></table>`;
    return h;
  }
  // PYC: single header row with payment breakdown
  const r = items[0];
  return `<table class="mt"><tbody>
    <tr><td class="r">Thành tiền</td><td class="r money">${formatVND(r.thanh_tien || r.so_tien)}</td></tr>
    <tr><td class="r">VAT</td><td class="r money">${formatVND(r.vat)}</td></tr>
    <tr class="sub"><td class="r">Sau VAT</td><td class="r money">${formatVND(r.sau_vat)}</td></tr>
    <tr><td class="r">(−) Giữ QT</td><td class="r money">${formatVND(r.giu_quyet_toan)}</td></tr>
    <tr><td class="r">(−) Giữ BH</td><td class="r money">${formatVND(r.giu_bao_hanh)}</td></tr>
    <tr><td class="r">(−) Trừ tạm ứng</td><td class="r money">${formatVND(r.tru_tam_ung)}</td></tr>
    <tr><td class="r">(−) Trừ khác</td><td class="r money">${formatVND(r.tru_khac)}</td></tr>
    <tr class="ttl"><td class="r">CHI TRONG KỲ</td><td class="r money">${formatVND(r.chi_trong_ky)}</td></tr>
  </tbody></table>`;
}

function mkTrail(g, nameOf) {
  const ord = { draft: 0, pending_l1: 1, pending_l2: 2, approved: 3 };
  const cur = ord[g.trang_thai] ?? 0;
  const rej = g.trang_thai === 'rejected';
  const steps = [
    { lb: 'Nháp',     w: nameOf ? nameOf(g.created_by) : g.created_by },
    { lb: 'Chờ duyệt', w: '' },
    { lb: 'Duyệt L1',  w: nameOf ? nameOf(g.approved_l1_by) : g.approved_l1_by },
    { lb: 'Hoàn tất',  w: nameOf ? nameOf(g.approved_l2_by) : g.approved_l2_by }
  ];
  return steps.map((s, i) => {
    let cls = '';
    if (rej) cls = i <= 1 ? 'done' : (i === 2 ? 'rej' : '');
    else cls = i < cur ? 'done' : (i === cur ? 'cur' : '');
    return `<div class="trail-s ${cls}"><div class="tl">${s.lb}</div>${cls === 'done' && s.w ? `<div class="tw">${escH(s.w)}</div>` : ''}${cls === 'rej' ? '<div class="tw">Từ chối</div>' : ''}</div>`;
  }).join('');
}

function dc(l,v,cls='') { return `<div class="dc ${cls}"><div class="dc-l">${l}</div><div class="dc-v">${escH(v)}</div></div>`; }
function escH(s) { return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
function fmtDate(s) { if (!s) return ''; try { return new Date(s).toLocaleString('vi-VN'); } catch(e) { return s; } }
function toast(msg, type='ok') {
  const c = document.getElementById('toast-container'); if (!c) { alert(msg); return; }
  const el = document.createElement('div');
  el.className = 'toast toast-' + type;
  el.textContent = msg;
  c.appendChild(el);
  setTimeout(() => el.remove(), 3500);
}
